import { FC } from 'react';
import { Modal, FileInput, Button, Typography } from 'melp-design/components';
import { Box, Stack } from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { ModalProps, useModalContext } from 'store/modal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import {
  EmployeesFilters,
  useExportEmployeeConsents,
  useImportEmployeeConsents,
} from 'store/employees';
import { FileDownloadOutlined } from '@mui/icons-material';
import { usePredefinedToasts } from 'utils/Toast';
import { SystemColors } from 'melp-design/style';
import { FileImportErrorsModal } from 'components/modals';
import consentsExample from './consents-example.png';

interface Values {
  file: File | undefined;
}

interface Props extends ModalProps {
  filters: Partial<EmployeesFilters>;
}

export const ImportConsentsModal: FC<Props> = ({ closeModal, filters }) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const { openModal } = useModalContext();

  const { control, handleSubmit } = useForm<Values>({
    defaultValues: { file: undefined },
    mode: 'onChange',
  });

  const { mutate: importConsents, status } = useImportEmployeeConsents();
  const { mutate: exportConsents, isLoading: isExporting } =
    useExportEmployeeConsents();

  const onSubmit: SubmitHandler<Values> = (values) => {
    if (values.file) {
      importConsents(
        { file: values.file },
        {
          onSuccess: ({ data }) => {
            closeModal({ action: 'CLOSE' });

            if (data.success) {
              predefinedToasts.success.updated();
            } else {
              openModal({
                component: FileImportErrorsModal,
                size: 'lg',
                props: {
                  title: t('employees.consents.actions.import'),
                  onBack: () =>
                    openModal({
                      component: ImportConsentsModal,
                      size: 'lg',
                      props: { filters },
                    }),
                  errors: data.errorMessages.map((error) => ({
                    row: error.row,
                    field: error.column,
                    message: error.message,
                  })),
                },
              });
            }
          },
        },
      );
    }
  };

  const steps = [
    {
      title: t('employees.consents.import.steps.download.title'),
      description: t('employees.consents.import.steps.download.description'),
      content: (
        <Box>
          <Button
            label={t('employees.consents.actions.export')}
            variant="neutral-outline"
            size="sm"
            icon={FileDownloadOutlined}
            loading={isExporting}
            onClick={() => exportConsents({ filters })}
          />
        </Box>
      ),
    },
    {
      title: t('employees.consents.import.steps.update.title'),
      description: t('employees.consents.import.steps.update.description'),
      content: (
        <Box
          component="img"
          src={consentsExample}
          alt={t('employees.consents.import.steps.update.title')}
          sx={{
            display: 'block',
            maxWidth: '100%',
            borderRadius: 1,
            border: `1px solid ${SystemColors.grey[90]}`,
          }}
        />
      ),
    },
    {
      title: t('employees.consents.import.steps.upload.title'),
      description: t('employees.consents.import.steps.upload.description'),
      content: (
        <Controller
          name="file"
          control={control}
          render={({ field: { name, onChange, ref } }) => (
            <FileInput
              name={name}
              onChange={([file]) => {
                onChange(file);
              }}
              accept={['xls', 'xlsx']}
              maxFiles={1}
              required
              ref={ref}
            />
          )}
        />
      ),
    },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('employees.consents.actions.import')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('actions.import'),
            disabled: status === 'loading',
            type: 'submit',
          },
        ]}
      >
        <Stack gap={2}>
          <Timeline
            sx={{
              m: 0,
              p: 0,
              '& .MuiTimelineItem-root:before': {
                flex: 0,
                p: 0,
              },
            }}
          >
            {steps.map((step, index) => (
              <TimelineItem key={index}>
                <TimelineSeparator>
                  <TimelineDot
                    sx={{
                      m: 0,
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: SystemColors.primary.base,
                      boxShadow: 'none',
                    }}
                  >
                    <Typography variant="h4" color="textWhite">
                      {index + 1}
                    </Typography>
                  </TimelineDot>
                  {index < steps.length - 1 ? <TimelineConnector /> : null}
                </TimelineSeparator>
                <TimelineContent sx={{ pr: 0, pb: 3 }}>
                  <Stack gap={1}>
                    <Box>
                      <Typography variant="h3">{step.title}</Typography>
                      <Typography variant="p2" color="textSecondary">
                        {step.description}
                      </Typography>
                    </Box>
                    {step.content}
                  </Stack>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Stack>
      </Modal.Content>
    </form>
  );
};
